import { collection, doc, getDoc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';

/** Normalize Greek ΑΦΜ to 9 digits (strips EL prefix). */
export function normalizeGreekVat(raw) {
  if (raw === undefined || raw === null) return '';
  let s = String(raw).replace(/\s/g, '').trim();
  if (!s) return '';
  const upper = s.toUpperCase();
  if (upper.startsWith('EL')) s = s.slice(2);
  const digits = s.replace(/\D/g, '');
  return digits.length === 9 ? digits : '';
}

/** Load all VAT → company name mappings for a company. */
export async function loadVatMappingsMap(companyId) {
  if (!companyId) return new Map();
  const snap = await getDocs(collection(db, 'companies', companyId, 'vatMappings'));
  const map = new Map();
  snap.docs.forEach((d) => {
    const data = d.data();
    const vat = normalizeGreekVat(data.vat || d.id);
    const name = (data.companyName || '').trim();
    if (vat && name) map.set(vat, name);
  });
  return map;
}

/** Apply a preloaded mappings map to invoice fields (sync). */
export function applyVatMappingSync(fields, mappingsMap) {
  const base = { ...(fields || {}) };
  delete base._nameFromMapping;

  const vat = normalizeGreekVat(base.issuer_vat);
  if (!vat || !mappingsMap?.size) {
    return { ...base, issuer_vat: vat || base.issuer_vat || '' };
  }

  const mappedName = mappingsMap.get(vat);
  if (!mappedName) {
    return { ...base, issuer_vat: vat };
  }

  return {
    ...base,
    issuer_vat: vat,
    issuer_company_name: mappedName,
    _nameFromMapping: true,
  };
}

/** Fetch a single mapping and apply (async). */
export async function applyVatMapping(companyId, fields) {
  const vat = normalizeGreekVat(fields?.issuer_vat);
  if (!vat || !companyId) return fields || {};

  const snap = await getDoc(doc(db, 'companies', companyId, 'vatMappings', vat));
  if (!snap.exists()) return { ...fields, issuer_vat: vat };

  const name = (snap.data().companyName || '').trim();
  if (!name) return { ...fields, issuer_vat: vat };

  return {
    ...fields,
    issuer_vat: vat,
    issuer_company_name: name,
    _nameFromMapping: true,
  };
}

/** Remove internal UI flags before persisting to Firestore. */
export function stripMappingMeta(fields) {
  if (!fields || typeof fields !== 'object') return fields;
  const { _nameFromMapping, ...clean } = fields;
  return clean;
}

/**
 * Build unified VAT rows from saved mappings + scanned invoices.
 * Each row: { id, vat, companyName, source, invoiceCount, scanNames, alternateNames, needsReview, hasMapping }
 */
export function buildVatRowsFromSources(mappingRows, invoices) {
  const byVat = new Map();

  mappingRows.forEach((m) => {
    byVat.set(m.vat, {
      id: m.id,
      vat: m.vat,
      companyName: m.companyName,
      source: 'mapping',
      hasMapping: true,
      invoiceCount: 0,
      scanNames: new Set(),
      needsReview: false,
      updatedAt: m.updatedAt || '',
      sampleInvoiceUrl: '',
      sampleInvoiceLabel: '',
    });
  });

  invoices.forEach((inv) => {
    const vat = normalizeGreekVat(inv.extracted?.issuer_vat);
    if (!vat) return;
    const name = (inv.extracted?.issuer_company_name || '').trim();

    if (!byVat.has(vat)) {
      byVat.set(vat, {
        id: vat,
        vat,
        companyName: name,
        source: 'scanned',
        hasMapping: false,
        invoiceCount: 0,
        scanNames: new Set(),
        needsReview: true,
        updatedAt: '',
        sampleInvoiceUrl: '',
        sampleInvoiceLabel: '',
      });
    }

    const row = byVat.get(vat);
    row.invoiceCount += 1;
    if (name) row.scanNames.add(name);

    if (inv.url) {
      const uploadedAt = inv.uploadedAt ? new Date(inv.uploadedAt).getTime() : 0;
      const currentAt = row._sampleUploadedAt || 0;
      if (!row.sampleInvoiceUrl || uploadedAt >= currentAt) {
        row.sampleInvoiceUrl = inv.url;
        row.sampleInvoiceLabel = inv.fileName || inv.extracted?.invoice_number || '';
        row._sampleUploadedAt = uploadedAt;
      }
    }
  });

  return Array.from(byVat.values())
    .map((row) => {
      const scanNames = Array.from(row.scanNames);
      const alternateNames = scanNames.filter((n) => n && n !== row.companyName);

      if (row.hasMapping && alternateNames.length > 0) {
        row.needsReview = true;
      }
      if (!row.hasMapping && scanNames.length > 0) {
        // Pick the most frequent scanned name as suggestion when unmapped
        const counts = {};
        invoices.forEach((inv) => {
          const v = normalizeGreekVat(inv.extracted?.issuer_vat);
          if (v !== row.vat) return;
          const n = (inv.extracted?.issuer_company_name || '').trim();
          if (n) counts[n] = (counts[n] || 0) + 1;
        });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        if (top && !row.companyName) row.companyName = top[0];
      }

      return {
        ...row,
        scanNames,
        alternateNames,
        _sampleUploadedAt: undefined,
      };
    })
    .sort((a, b) => {
      if (a.needsReview !== b.needsReview) return a.needsReview ? -1 : 1;
      return (a.companyName || a.vat).localeCompare(b.companyName || b.vat, 'el');
    });
}

/** Update issuer name on all invoices matching a VAT. Returns count updated. */
export async function updateInvoicesForVat(companyId, vat, companyName) {
  if (!companyId || !vat || !companyName) return 0;

  const snap = await getDocs(collection(db, 'companies', companyId, 'invoices'));
  let updated = 0;
  let batch = writeBatch(db);
  let batchSize = 0;

  const commitBatch = async () => {
    if (batchSize === 0) return;
    await batch.commit();
    batch = writeBatch(db);
    batchSize = 0;
  };

  for (const d of snap.docs) {
    const data = d.data();
    const invVat = normalizeGreekVat(data.extracted?.issuer_vat);
    if (invVat !== vat) continue;

    const currentName = (data.extracted?.issuer_company_name || '').trim();
    if (currentName === companyName && invVat === vat) continue;

    batch.update(d.ref, {
      extracted: {
        ...(data.extracted || {}),
        issuer_vat: vat,
        issuer_company_name: companyName,
      },
    });
    updated += 1;
    batchSize += 1;

    if (batchSize >= 400) await commitBatch();
  }

  await commitBatch();
  return updated;
}

